import React from 'react';
import { useNavigate } from 'react-router-dom';
import StatCard from './StatCard';
import { paymentTypeConfig, formatMoney, formatDate, isOverdue, getDaysUntilDue } from './accountingConstants';

export default function OverviewTab({
  overviewStats,
  kasaSaldo,
  allInvoices,
  kasaOperations,
  markingPaid,
  markAsPaid,
  setInvoiceFilters,
  setActiveTab
}) {
  const navigate = useNavigate();

  return (
    <div>
      {/* Statystyki glowne */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
        gap: '0.75rem',
        marginBottom: '1.5rem'
      }}>
        <StatCard
          label="Przeterminowane"
          value={overviewStats.overdueCount}
          icon={'⚠️'}
          borderColor="var(--danger)"
          subValue={formatMoney(overviewStats.overdueValue)}
          onClick={() => { setInvoiceFilters(f => ({...f, paymentStatus: 'overdue'})); setActiveTab('invoices'); }}
        />
        <StatCard
          label="Do zapłaty (7 dni)"
          value={overviewStats.dueSoonCount}
          icon={'📅'}
          borderColor="var(--warning)"
          subValue={formatMoney(overviewStats.dueSoonValue)}
          onClick={() => { setInvoiceFilters(f => ({...f, paymentStatus: 'unpaid'})); setActiveTab('invoices'); }}
        />
        <StatCard
          label="Suma do zapłaty"
          value={formatMoney(overviewStats.totalToPay)}
          icon={'💰'}
          borderColor="var(--info)"
          onClick={() => setActiveTab('invoices')}
        />
        <StatCard
          label="Przychody (miesiąc)"
          value={formatMoney(overviewStats.monthlyIncome)}
          icon={'📈'}
          borderColor="var(--success)"
          onClick={() => setActiveTab('cash-flow')}
        />
        <StatCard
          label="Wydatki (miesiąc)"
          value={formatMoney(overviewStats.monthlyExpense)}
          icon={'📉'}
          borderColor="var(--danger)"
          onClick={() => setActiveTab('cash-flow')}
        />
        <StatCard
          label="Bilans (miesiąc)"
          value={formatMoney(overviewStats.monthlyBalance)}
          icon={'📊'}
          borderColor={overviewStats.monthlyBalance >= 0 ? 'var(--success)' : 'var(--danger)'}
        />
      </div>

      {/* Salda kont */}
      {kasaSaldo && (
        <div style={{
          backgroundColor: "var(--card-bg)",
          borderRadius: '6px',
          border: '1px solid var(--border)',
          padding: '1rem',
          marginBottom: '1.5rem'
        }}>
          <h3 style={{ fontSize: '13px', fontWeight: 600, marginBottom: '0.75rem', color: 'var(--text-secondary)' }}>
            {'💳'} Salda kont
          </h3>
          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
            {Object.entries(paymentTypeConfig).map(([key, config]) => {
              const saldo = kasaSaldo[key] || kasaSaldo[`saldo_${key}`] || 0;
              return (
                <div key={key} style={{
                  padding: '0.5rem 1rem',
                  backgroundColor: 'var(--bg)',
                  borderRadius: '4px',
                  borderLeft: `3px solid ${config.color}`,
                  minWidth: '120px'
                }}>
                  <div style={{ fontSize: '14px', fontWeight: 600, color: config.color }}>
                    {formatMoney(saldo)}
                  </div>
                  <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>
                    {config.icon} {config.label}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Sekcje side-by-side */}
      <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
        {/* Faktury przeterminowane */}
        <div style={{
          flex: '1 1 400px',
          backgroundColor: "var(--card-bg)",
          borderRadius: '6px',
          border: '1px solid var(--border)',
          overflow: 'hidden'
        }}>
          <div style={{
            padding: '0.75rem 1rem',
            backgroundColor: 'var(--card-bg)', border: '1px solid var(--border)', color: 'var(--text)' ,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <span style={{ fontWeight: 600, fontSize: '13px' }}>{'⚠️'} Faktury przeterminowane</span>
            <span style={{ fontSize: '11px', opacity: 0.9 }}>{overviewStats.overdueCount} faktur</span>
          </div>
          <div style={{ padding: '0.5rem', maxHeight: '250px', overflowY: 'auto' }}>
            {(Array.isArray(allInvoices) ? allInvoices : []).filter(inv => isOverdue(inv)).slice(0, 5).map(invoice => (
              <div
                key={invoice.id}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '8px 10px',
                  backgroundColor: 'var(--danger-light)',
                  borderRadius: '4px',
                  border: '1px solid var(--danger-light)',
                  marginBottom: '4px'
                }}
              >
                <div
                  onClick={() => navigate(`/purchase-invoices/${invoice.id}`)}
                  style={{ cursor: 'pointer', flex: 1 }}
                >
                  <div style={{ fontWeight: 500, fontSize: '12px' }}>{invoice.numer_faktury}</div>
                  <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>{invoice.dostawca_nazwa}</div>
                </div>
                <div style={{ textAlign: 'right', marginRight: '8px' }}>
                  <div style={{ fontWeight: 600, fontSize: '12px', color: 'var(--danger)' }}>
                    {formatMoney(invoice.suma_brutto)}
                  </div>
                  <div style={{ fontSize: '10px', color: 'var(--danger)' }}>
                    {getDaysUntilDue(invoice.data_platnosci)} dni po terminie
                  </div>
                </div>
                <button
                  onClick={(e) => markAsPaid(invoice, e)}
                  disabled={markingPaid === invoice.id}
                  style={{
                    padding: '4px 10px',
                    background: markingPaid === invoice.id ? 'var(--text-muted)' : 'var(--success)',
                    color: '#fff' ,
                    fontSize: '11px',
                    cursor: markingPaid === invoice.id ? 'wait' : 'pointer',
                    whiteSpace: 'nowrap'
                  }}
                  title="Oznacz jako zapłacono"
                >
                  {markingPaid === invoice.id ? '⏳' : '✅'}
                </button>
              </div>
            ))}
            {overviewStats.overdueCount === 0 && (
              <div style={{ textAlign: 'center', padding: '1rem', color: 'var(--text-muted)', fontSize: '12px' }}>
                {'✅'} Brak przeterminowanych faktur
              </div>
            )}
          </div>
        </div>

        {/* Ostatnie operacje */}
        <div style={{
          flex: '1 1 400px',
          backgroundColor: "var(--card-bg)",
          borderRadius: '6px',
          border: '1px solid var(--border)',
          overflow: 'hidden'
        }}>
          <div style={{
            padding: '0.75rem 1rem',
            backgroundColor: 'var(--card-bg)', border: '1px solid var(--border)', color: 'var(--text)' ,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <span style={{ fontWeight: 600, fontSize: '13px' }}>{'💰'} Ostatnie operacje</span>
            <button
              onClick={() => setActiveTab('cash-flow')}
              style={{
                background: 'var(--bg-secondary)',
                // var(--text): bialy tekst byl niewidoczny na jasnym tle bg-secondary
                color: 'var(--text)',
                padding: '4px 10px',
                fontSize: '11px',
                cursor: 'pointer'
              }}
            >
              Zobacz wszystkie {'→'}
            </button>
          </div>
          <div style={{ padding: '0.5rem', maxHeight: '250px', overflowY: 'auto' }}>
            {(Array.isArray(kasaOperations) ? kasaOperations : []).slice(0, 8).map(op => (
              <div
                key={op.id}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '8px 10px',
                  backgroundColor: op.typ_operacji === 'KP' ? 'var(--success-light)' : 'var(--danger-light)',
                  borderRadius: '4px',
                  border: `1px solid ${op.typ_operacji === 'KP' ? 'var(--success-light)' : 'var(--danger-light)'}`,
                  marginBottom: '4px'
                }}
              >
                <div>
                  <div style={{ fontWeight: 500, fontSize: '12px' }}>
                    {op.typ_operacji === 'KP' ? '📥' : '📤'} {op.opis || op.kategoria}
                  </div>
                  <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>
                    {formatDate(op.data_operacji)} {'•'} {paymentTypeConfig[op.typ_platnosci]?.label || op.typ_platnosci}
                  </div>
                </div>
                <div style={{
                  fontWeight: 600,
                  fontSize: '12px',
                  color: op.typ_operacji === 'KP' ? 'var(--success)' : 'var(--danger)'
                }}>
                  {op.typ_operacji === 'KP' ? '+' : '-'}{formatMoney(op.kwota)}
                </div>
              </div>
            ))}
            {(!Array.isArray(kasaOperations) || kasaOperations.length === 0) && (
              <div style={{ textAlign: 'center', padding: '1rem', color: 'var(--text-muted)', fontSize: '12px' }}>
                Brak operacji
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
